import { ref, computed } from 'vue'
import { teamService } from '@/api/teams'
import {
  showSuccess,
  showWarning,
  showError,
  showRequiredFieldsModal,
  showRefundDataModal,
} from '@/services/notification'

/**
 * Provides reusable checks for mandatory schema responses and refund data completeness.
 *
 * @param {Object} options
 * @param {import('vue').Ref} options.selectedTeam - Reactive reference to the currently selected team.
 * @param {import('vue').Ref} options.currentUser - Reactive reference to the current user object.
 * @param {Function} [options.loadTeamById] - Optional loader to refresh the current team.
 * @param {Function} [options.refreshTeams] - Optional refresher to sync the teams list.
 * @returns {Object} integrity helpers
 */
export const useTeamIntegrityChecks = ({
  selectedTeam,
  currentUser,
  loadTeamById,
  refreshTeams,
  persistDismissal = true,
}) => {
  const refundModalShown = ref(false)
  const refundModalDismissed = ref(new Set())

  const isAdminOrCreator = computed(() => {
    if (!selectedTeam?.value || !currentUser?.value) return false

    return (
      selectedTeam.value.role === 'teamAdmin' ||
      selectedTeam.value.creatorId === currentUser.value.id
    )
  })

  const hasMissingRequiredFields = computed(() => {
    if (!selectedTeam?.value?.fields || !currentUser?.value) return false

    return selectedTeam.value.fields.some((field) => {
      if (!field.required) return false

      if (field.responses && Array.isArray(field.responses)) {
        const userResponse = field.responses.find(
          (response) => response.userId === currentUser.value.id,
        )
        return !userResponse || !userResponse.data || userResponse.data === ''
      }

      if (Array.isArray(field.response)) {
        return field.response.length === 0 || !field.response[0]?.data
      }

      return true
    })
  })

  const saveFieldValue = async (field, value) => {
    await teamService.updateTeamFieldResponse(field.id, value)

    if (selectedTeam?.value?.id) {
      if (typeof loadTeamById === 'function') {
        await loadTeamById(selectedTeam.value.id)
      }
      if (typeof refreshTeams === 'function') {
        await refreshTeams()
      }
    }

    if (field.responses && Array.isArray(field.responses)) {
      const existingResponse = field.responses.find(
        (response) => response.userId === currentUser.value.id,
      )

      if (existingResponse) {
        existingResponse.data = value
      } else {
        field.responses.push({
          userId: currentUser.value.id,
          data: value,
        })
      }
    } else if (Array.isArray(field.response)) {
      if (field.response.length > 0) {
        field.response[0].data = value
      } else {
        field.response.push({ data: value })
      }
    }
  }

  const checkMissingRequiredFields = async () => {
    if (!selectedTeam?.value || !currentUser?.value) return true
    if (isAdminOrCreator.value) return true
    if (!selectedTeam.value.fields || !hasMissingRequiredFields.value) return true

    const missingFields = selectedTeam.value.fields.filter((field) => {
      if (!field.required) return false

      if (field.responses && Array.isArray(field.responses)) {
        const userResponse = field.responses.find(
          (response) => response.userId === currentUser.value.id,
        )
        return !userResponse || !userResponse.data || userResponse.data === ''
      }

      if (Array.isArray(field.response)) {
        return field.response.length === 0 || !field.response[0]?.data
      }

      return true
    })

    if (missingFields.length === 0) return true

    let completed = false

    const result = await showRequiredFieldsModal(
      missingFields,
      async (values) => {
        for (let i = 0; i < missingFields.length; i += 1) {
          const field = missingFields[i]
          const value = values[i]
          await saveFieldValue(field, value)
        }
        showSuccess('Alle Pflichtfelder wurden ausgefüllt!')
        completed = true
      },
      () => {},
    )

    if (result?.action !== 'complete') {
      return false
    }

    return completed
  }

  const checkRefundDataRequirements = async () => {
    if (!selectedTeam?.value || !currentUser?.value) return true

    const teamId = selectedTeam.value.id
    if (!teamId) return true
    if (refundModalShown.value) return false
    if (persistDismissal && refundModalDismissed.value.has(teamId)) return false

    const status = selectedTeam.value.status
    if (status !== 'pro' && status !== 'ultimate') {
      return true
    }

    try {
      const refundSettings = await teamService.getTeamRefundSettings(teamId)

      if (!refundSettings.enabled) return true

      const hasAvailableMethods =
        refundSettings.paypalEnabled ||
        refundSettings.bankTransferEnabled ||
        refundSettings.cashEnabled

      if (!hasAvailableMethods) return true

      const currentData = await teamService.getUserRefundData(teamId)

      if (currentData && currentData.refundMethod) {
        let isValid = true

        if (currentData.refundMethod === 'paypal' && !currentData.paypalMeName) {
          isValid = false
        } else if (currentData.refundMethod === 'bank_transfer') {
          if (!currentData.iban || !currentData.bic || !currentData.accountHolder) {
            isValid = false
          }
        } else if (currentData.refundMethod === 'cash') {
          isValid = true
        }

        if (isValid) return true
      }

      refundModalShown.value = true

      const result = await showRefundDataModal(
        selectedTeam.value.name,
        refundSettings,
        { ...currentData, teamId },
        async () => {
          showSuccess('Erstattungsdaten wurden konfiguriert!')
          refundModalShown.value = false
        },
        () => {
          showWarning(
            'Erstattungsdaten wurden nicht konfiguriert. Du kannst dies später in den Bereich-Einstellungen nachholen.',
          )
          refundModalShown.value = false
          if (persistDismissal) {
            refundModalDismissed.value.add(teamId)
          }
        },
      )

      if (result?.action === 'redirect') {
        return false
      }

      return false
    } catch (error) {
      console.error('Fehler beim Prüfen der Erstattungsdaten:', error)
      refundModalShown.value = false
      showError('Fehler beim Laden der Erstattungsdaten. Bitte versuche es später erneut.')
      return false
    }
  }

  return {
    hasMissingRequiredFields,
    isAdminOrCreator,
    checkMissingRequiredFields,
    checkRefundDataRequirements,
  }
}
